import useAuth from '@app/auth/use-auth';
import { useGetFilterLabels } from '@app/components/data-table/hooks/use-get-filter-labels';
import PreviewSheet from '@app/components/preview/preview-sheet';
import { PreviewSheetBarChart } from '@app/components/preview/preview-sheet-bar-chart';
import PreviewSheetSection from '@app/components/preview/preview-sheet-chart-section';
import { useAccount } from '@shared/api';
import { useGetPurchasedFoodAndDrinkPreview } from '@shared/api/hooks/purchased-goods/food-and-drink';
import { itemsByRatingChartConfig } from '@shared/components/chart/configs';
import {
  EmissionsCategoryEnum,
  PurchasedGoodsFoodPreviewRetrieveParams,
} from '@shared/api/types';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatCompact, getCurrencySymbol } from '@shared/lib/format';
import {
  capitalizeFirstLetter,
  formatNumber,
  RatingToTextEnum,
} from '@shared/lib/utils';
import { useQueryStates } from 'nuqs';
import { useState } from 'react';
import { buildFoodFilterParams, foodFilters } from '../filters';

export default function PurchasedFoodAndDrinkPreviewSheet() {
  const [isOpen, setIsOpen] = useState(false);

  const { flatLabels: tags } = useGetFilterLabels(
    Object.values(foodFilters).map((f) => f.column)
  );

  // Retrieve query params from URL
  const schema = Object.fromEntries(
    Object.values(foodFilters).map((f) => [f.column, f.type])
  );
  const [query] = useQueryStates(schema);

  // Map query params to API params
  const params: PurchasedGoodsFoodPreviewRetrieveParams =
    buildFoodFilterParams(query);

  const { data, status, isError } = useGetPurchasedFoodAndDrinkPreview({
    params,
    enabled: true,
  });

  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);

  const totalEmissionsTonnes = data?.totalEmissionsTonnes;
  const totalWeightKg = data?.totalWeightKg;
  const totalSpend = data?.totalSpend != null ? Number(data.totalSpend) : null;

  const emissionsByCategory = data?.foodEmissionsByCategory
    ?.slice(0, 10)
    ?.map((category) => {
      return {
        category: capitalizeFirstLetter(category.category),
        emissions: category.totalEmissionsTonnes,
        fill: 'var(--chart-3)',
      };
    });

  const itemsByRating = data?.itemsByRating
    ?.map((category) => {
      return {
        category: category.rating,
        items: category.numberOfItems,
        fill: `var(--rating-${category.rating.toLowerCase()})`,
      };
    })
    .sort((a, b) => a.category.localeCompare(b.category));

  const numberOfItems = data?.itemsByRating?.reduce((acc, category) => {
    return acc + category.numberOfItems;
  }, 0);

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-1))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  return (
    <PreviewSheet
      title="Food and drink items"
      status={status}
      metrics={[
        {
          title: 'Total emissions',
          value: formatNumber(totalEmissionsTonnes!),
          unit: 'tCO2e',
        },
        {
          title: 'Total weight of items',
          value: formatNumber(totalWeightKg! / 1000),
          unit: 't',
        },
        ...(hasCostFeature && totalSpend != null
          ? [
              {
                title: 'Total spend',
                value: (
                  <>
                    {/* Currency symbol matches the gray-400 unit colour so it
                        reads as a unit rather than blending into the figure. */}
                    <span className="mr-1 text-gray-400">
                      {getCurrencySymbol(account?.defaultCurrency)}
                    </span>
                    {formatCompact(totalSpend)}
                  </>
                ),
                unit: '',
              },
            ]
          : []),
      ]}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      tags={tags}
    >
      {isError && <Text className="text-error">Error loading data</Text>}
      {data && (
        <>
          <PreviewSheetSection title="Items by rating">
            <div className="flex items-center space-y-2 overflow-visible">
              <div className="relative overflow-visible">
                <SimplePieChart
                  dataKey="items"
                  nameKey="category"
                  chartConfig={itemsByRatingChartConfig}
                  chartData={itemsByRating!.reverse()}
                  innerRadius={50}
                  className="h-40 w-52"
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  centerLabel={{
                    value: numberOfItems!,
                    label: 'items',
                  }}
                />
              </div>
              <div className="flex  flex-col gap-y-2">
                {(
                  ['A', 'B', 'C', 'D', 'E'] as Array<
                    keyof typeof RatingToTextEnum
                  >
                ).map((rating, idx) => {
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <RatingCloud
                        rating={rating as EmissionsCategoryEnum}
                        className="w-9"
                      />
                      <Text>{RatingToTextEnum[rating]}</Text>
                    </div>
                  );
                })}
              </div>
            </div>
          </PreviewSheetSection>

          {/* One bar says nothing the totals above don't; the breakdown
              needs at least two buckets to be worth the space. */}
          {(emissionsByCategory?.length ?? 0) > 1 && (
            <PreviewSheetSection
              title="Emissions by category"
              unit="tCO2e"
              className="h-full"
            >
              <PreviewSheetBarChart
                nameKey="category"
                chartData={emissionsByCategory}
                chartConfig={chartConfig}
              />
            </PreviewSheetSection>
          )}
        </>
      )}
    </PreviewSheet>
  );
}
